import { DigitalGpSubmission } from '@sky-distribution/digital-gp-submission';
import {
  bindDigital,
  calculateDigital,
  createDigital,
  getDigitalTrackingCodesFromSubmission,
  proposeDigital,
  updateDigital,
} from '@sky-distribution/gp-digital-submission-service';
import {
  getDiscountCodeFromTrackingCodes,
  getTrackingCodesFromSubmission,
  Submission,
} from '@sky-distribution/gp-onboarding-policy-service';
import {
  flattenDotNotationConsentValues,
  getSearchParams,
  isPathSameAsRoute,
  isRouteHasPath,
  redirect,
  safeWindow,
} from '@sky-distribution/gp-onboarding-shared-utils';
import { assign } from 'xstate';
import { copySubmissionDefaults } from '../actions/copySubmissionDefaults';
import { saveDataFrom } from '../actions/saveDataFrom';
import {
  saveDefaultDataFromSearchParams,
} from '../actions/saveDefaultDataFromSearchParams/saveDefaultDataFromSearchParams';
import { saveDiscountCodeFromSearchParams } from '../actions/saveDiscountCodeFromSearchParams';
import { switchProductsCoverageDetails } from '../actions/switchProductsCoverageDetails';
import {
  isBuyServiceEvent,
  isCalculateServiceEvent,
  isChangeRouteEvent,
  isCreateServiceEvent,
  isMetaDataServiceEvent,
  isNextQuoteEvent,
  isQuoteServiceEvent,
  isRateServiceEvent,
  isSaveDataEvent,
} from '../events';
import {
  cantInsure,
  earlyRenewalError,
  hasCoverage,
  isContextPropertyEqual,
  isInvalidOtp,
  isLoggedIn,
  isPolicyInState,
  isUnauthorized,
  lateRenewalError,
  searchParamExist,
} from '../guards';
import { isPath } from '../guards/isPath';
import {
  bindWithOTP,
  buy,
  create,
  getProductMetaData,
  quote,
  quoteRenewal,
  rate,
  retrieveSales,
  update,
} from '../services/services';
import { AppStateMachineContext, AppStateMachineOptions } from '../types';
import { SALES_PORTAL_LOGIN_URL } from './config';
import { RoutePath } from './routePath';
import { clearSearchParams } from './utils/clearSearchParams.util';
import { mergeDigitalGpSubmission, mergeProductMetaData, mergeRateVariants } from './utils/saveResponseData.util';
import { clearTrackingCodesFromUrl, getDigitalTrackingCodes, getTrackingCodes } from './utils/trackingCodes.util';

const HOUSEHOLD_PRODUCT_CODE = 'hoh';

export const getDefaultMachineOptions = (
  path: string,
  initialContext: AppStateMachineContext,
): AppStateMachineOptions => ({
  actions: {
    saveData: assign((context, event) => {
      if (isNextQuoteEvent(event)) {
        return {
          ...context,
          submission: event.data.submission,
          ids: event.data.ids ?? context.ids,
          digitalSubmission: event.data.digitalSubmission ?? context.digitalSubmission,
          digitalOffers: event.data.digitalOffers ?? context.digitalOffers,
          klcPricedDigitalOffers: event.data.klcPricedDigitalOffers ?? context.klcPricedDigitalOffers,
          submissionLocalData: event.data.submissionLocalData ?? context.submissionLocalData,
          submissionConditions: event.data.submissionConditions ?? context.submissionConditions,
          language: event.data.language ?? context.language,
          otpCode: event.data.otpCode ?? context.otpCode,
          totalPrice: event.data.totalPrice ?? context.totalPrice,
          consents: event.data.consents ? flattenDotNotationConsentValues(event.data.consents) : context.consents,
          rateData: event.data.rateData ?? context.rateData,
        };
      }

      if (isSaveDataEvent(event)) {
        return {
          ...context,
          ...event.data,
        };
      }

      return {
        ...context,
      };
    }),
    saveDataFrom,
    copySubmissionDefaults,
    resetContext: assign((context) => {
      const emptyContext = Object.keys(context)
        .reduce((newContext, contextKey) => (
          { ...newContext, [contextKey]: undefined }
        ), {});

      return {
        ...emptyContext,
        ...initialContext,
      };
    }),
    changeRoute: assign((context, event) => {
      if (isChangeRouteEvent(event)) {
        return {
          ...context,
          route: event.data.routePath,
        };
      }

      return context;
    }),
    switchProductsCoverageDetails: assign((context) => ({
      ...switchProductsCoverageDetails(context),
    })),
    clearSearchParams: () => clearSearchParams(),
    clearTrackingCodesFromSearchParams: () => clearTrackingCodesFromUrl(),
    saveTrackingCodes: assign({
      trackingCodes: getTrackingCodes(),
      digitalTrackingCodes: getDigitalTrackingCodes(),
    }),
    saveDigitalTrackingCodes: assign({
      digitalTrackingCodes: getDigitalTrackingCodes(),
    }),
    saveSearchParams: assign({
      searchParams: Object.fromEntries(getSearchParams()
        .entries()),
    }),
    saveSearchParamsLowerCase: assign({
      searchParams: Object.fromEntries(Array.from(getSearchParams()
        .entries())
        .map(([key, value]) => [key, value.toLowerCase()])),
    }),
    saveDiscountCodeFromSearchParams: assign((context) => ({
      ...context,
      ...saveDiscountCodeFromSearchParams(context),
    })),
    saveRetrieveId: assign((context) => {
      const params = getSearchParams();
      const jobUUID = params.get('jobUUID');

      if (jobUUID) {
        return {
          ...context,
          ids: {
            jobUUID,
          },
        };
      }

      return context;
    }),
    saveDefaultDataFromSearchParams: assign((context, _event, meta) => ({
      ...context,
      ...saveDefaultDataFromSearchParams(context, meta.action.arguments),
    })),
    saveResponseData: assign((context, event) => {
      if (isMetaDataServiceEvent(event)) {
        return mergeProductMetaData(context, event);
      }

      if (isRateServiceEvent(event)) {
        return mergeRateVariants(context, event);
      }

      if (isQuoteServiceEvent(event)) {
        const trackingCodes = getTrackingCodesFromSubmission(event.data.data as Submission);
        const digitalTrackingCodes = getDigitalTrackingCodesFromSubmission(
          event.data.data as DigitalGpSubmission,
        );
        const discountCode = getDiscountCodeFromTrackingCodes(trackingCodes);

        return {
          ...context,
          trackingCodes,
          digitalTrackingCodes,
          discountCode,
          submission: event.data.data as Submission,
          ids: event.data.ids,
        };
      }

      if (isCreateServiceEvent(event)) {
        const trackingCodes = getTrackingCodesFromSubmission(event.data.data as Submission);
        const digitalTrackingCodes = getDigitalTrackingCodesFromSubmission(
          event.data.data as DigitalGpSubmission,
        );
        const discountCode = getDiscountCodeFromTrackingCodes(trackingCodes);

        return {
          ...context,
          trackingCodes,
          digitalTrackingCodes,
          discountCode,
          submission: event.data.data as Submission,
        };
      }

      if (isCalculateServiceEvent(event)) {
        return mergeDigitalGpSubmission(context, event);
      }

      return context;
    }),
    redirectToPayment: (_context, event) => {
      if (isBuyServiceEvent(event)) {
        redirect({ url: event.data.paymentUrl });
      }
    },
    redirectToLoginPage: () => {
      redirect({
        url: SALES_PORTAL_LOGIN_URL,
        searchParams: {
          returnPage: safeWindow()?.location?.href ?? '/',
        },
      });
    },
  },
  services: {
    createService: async ({
      submission, productCode, trackingCodes, consents, consentDictionary, tenant, consentConfiguration,
    }) => create(
      submission,
      productCode,
      consents,
      consentDictionary,
      trackingCodes,
      tenant,
      productCode === HOUSEHOLD_PRODUCT_CODE ? undefined : consentConfiguration?.context?.[0],
    ),
    updateService: async ({
      submission, ids, productCode, trackingCodes, consents, consentDictionary,
    }) => update(
      submission,
      ids,
      productCode,
      consents,
      consentDictionary,
      trackingCodes,
    ),
    quoteService: async ({
      submission,
      ids,
      productCode,
      trackingCodes,
      consents,
      consentDictionary,
      consentConfiguration,
      tenant,
      route,
    }) => quote(
      submission,
      ids,
      productCode,
      consents,
      consentDictionary,
      trackingCodes,
      productCode === HOUSEHOLD_PRODUCT_CODE && route === RoutePath.Summary
        ? consentConfiguration?.context?.[0]
        : undefined,
      productCode === HOUSEHOLD_PRODUCT_CODE ? tenant : undefined,
    ),
    quoteRenewalService: async ({
      ids, productCode, consents, consentDictionary,
    }) => quoteRenewal(ids, productCode, consents, consentDictionary),
    buyService: async ({
      submission, ids, totalPrice, productCode, returnUrl, brand,
    }) => buy(submission, ids, productCode, totalPrice, returnUrl, brand),
    bindWithOTPService: async ({
      submission, ids, otpCode, totalPrice, productCode,
    }) => bindWithOTP(submission, ids, productCode, otpCode, totalPrice),
    rateService: async ({ submission, rateData, productCode }) => rate({
      discount: submission.discount,
      ...rateData,
    }, productCode),
    retrieveSalesService: async ({ ids, productCode }) => retrieveSales(ids, productCode),
    metaDataService: async ({ rateData }) => getProductMetaData(rateData),
    calculateDigitalSubmission: async ({
      digitalSubmission, productCode, tenant, digitalTrackingCodes,
    }) => calculateDigital(digitalSubmission, productCode, tenant, digitalTrackingCodes),
    createDigitalSubmission: async ({
      digitalSubmission, productCode, tenant, digitalTrackingCodes, consents, consentDictionary,
    }) => createDigital(
      digitalSubmission, productCode, tenant, consents, consentDictionary,
      digitalTrackingCodes,
    ),
    updateDigitalSubmission: async ({
      digitalSubmission, ids, productCode, tenant, digitalTrackingCodes,
      consents, consentDictionary,
    }) => updateDigital(
      digitalSubmission, ids, productCode, tenant, consents, consentDictionary,
      digitalTrackingCodes,
    ),
    proposeDigitalSubmission: async ({
      digitalSubmission, ids, totalPrice, productCode, tenant, returnUrl,
    }) => proposeDigital(
      digitalSubmission, ids, productCode, tenant, totalPrice, returnUrl,
    ),
    bindDigitalSubmission: async ({
      digitalSubmission, ids, productCode, tenant, otpCode, totalPrice,
    }) => bindDigital(digitalSubmission, ids, productCode, tenant, otpCode, totalPrice),
  },
  guards: {
    isLoggedIn: () => isLoggedIn(),
    isNotFound: () => !isRouteHasPath(path, RoutePath),
    isSuccess: () => isPathSameAsRoute(path, RoutePath.ThankYou),
    isGenericError: () => isPathSameAsRoute(path, RoutePath.Error),
    isEarlyRenewalError: () => isPathSameAsRoute(path, RoutePath.EarlyRenewalError),
    isLateRenewalError: () => isPathSameAsRoute(path, RoutePath.LateRenewalError),
    isPath: (_context, _event, { cond }) => isPath(path, cond),
    isPolicyDraft: (_context, _event, { state }) => isPolicyInState(state, 'draft'),
    isPolicyQuoted: (_context, _event, { state }) => isPolicyInState(state, 'quoted'),
    isPolicyInitial: (_context, _event, { state }) => isPolicyInState(state, 'initial'),
    cantInsure: (_context, event) => cantInsure(event),
    earlyRenewalError: (_context, event) => earlyRenewalError(event),
    lateRenewalError: (_context, event) => lateRenewalError(event),
    isUnauthorized: (_context, event) => isUnauthorized(event),
    isInvalidOtp: (_context, event) => isInvalidOtp(event),
    isRetrieveSales: () => {
      const params = getSearchParams();
      const hasJobUUID = !!params.get('jobUUID');
      const hasSalesSource = params.get('source') === 'sales';

      return isPathSameAsRoute(path, RoutePath.Retrieve) && hasJobUUID && hasSalesSource;
    },
    isContextPropertyEqual: (context, _event, { cond }) => isContextPropertyEqual(context, cond),
    searchParamExist: (context, _event, { cond }) => searchParamExist(context, cond),
    hasCoverage: (context, _event, { cond }) => hasCoverage(context, cond),
  },
});
